import { format } from 'date-fns';
import { useMyActiveMembers } from '@/hooks/members/useMyActiveMembers';
import type { MemberDataset } from '@/lib/member-views/memberDataset.types';
import { sortFnTags } from '@/lib/member-views/sortRows';
import type { ActiveMemberRow } from '@/lib/members/members.types';
import { reportOnly as reportOnlyFor } from './reportOnly';

const reportOnly = reportOnlyFor<ActiveMemberRow>;

/**
 * All Active Members — the directory's default view: every Active and Suspended member, in the
 * server's last-name order until a column is sorted.
 *
 * The report is the 23 columns below, in `csv.order`. DOB, Established and Last AWP are the table's
 * own fields; the table's Patient and Phone combine two values each, so the report has its own
 * name and phone columns.
 */
export const allActiveMembersDataset: MemberDataset<ActiveMemberRow> = {
  key: 'all-active',
  rowKey: (row) => row.id,
  useRows: useMyActiveMembers,
  alphaKey: (row) => row.lastName,
  // The API pages through Salesforce itself and returns the whole roster in one response. 10,000 is
  // the provisional row count past which a view would move to server-side paging, pending the
  // production maximum per practice; nothing enforces it.
  loading: { mode: 'full', ceiling: 10_000 },
  // Local date, not `toISOString()`, which is already tomorrow on a US evening.
  csvFileName: ({ date }) => `My_Patient_All_Active_Members_${format(date, 'yyyy-MM-dd')}.csv`,
  fields: [
    {
      id: 'patient',
      label: 'memberViews.columns.patient',
      width: 'xl',
      // Sorts on "last first"; the cell shows the name the other way round, with the MDVIP ID.
      value: (row) => [row.lastName, row.firstName].filter(Boolean).join(' ') || null,
      table: true,
      sortable: true,
    },
    {
      id: 'dob',
      label: 'memberViews.columns.dob',
      width: 'sm',
      value: (row) => row.dateOfBirth,
      format: 'date',
      table: true,
      sortable: true,
      csv: { order: 11, label: 'memberViews.csv.columns.dob' },
    },
    {
      id: 'established',
      label: 'memberViews.columns.established',
      width: 'md',
      value: (row) => row.memberSince,
      format: 'date',
      table: true,
      sortable: true,
      csv: { order: 4, label: 'memberViews.csv.columns.dateEstablished' },
    },
    {
      id: 'phone',
      label: 'memberViews.columns.phone',
      width: 'lg',
      value: (row) => row.mobilePhone ?? row.phone,
      format: 'phone',
      table: true,
    },
    {
      id: 'lastAwp',
      label: 'memberViews.columns.lastAwp',
      width: 'sm',
      value: (row) => row.lastAwpDate,
      format: 'date',
      table: true,
      sortable: true,
      csv: { order: 9, label: 'memberViews.csv.columns.lastAwpDate' },
    },
    {
      id: 'priorityTags',
      label: 'memberViews.columns.priorityTags',
      width: 'xl',
      // No tags is a missing value, so untagged rows sort last both ways, as the design asks.
      value: (row) => (row.priorityTags.length > 0 ? row.priorityTags : null),
      table: true,
      sortable: true,
      sortFn: sortFnTags,
    },
    reportOnly('lastName', 1, 'memberViews.csv.columns.lastName', (row) => row.lastName),
    reportOnly('firstName', 2, 'memberViews.csv.columns.firstName', (row) => row.firstName),
    reportOnly('mdvipId', 3, 'memberViews.csv.columns.mdvipId', (row) => row.mdvipId),
    reportOnly('renewalDate', 5, 'memberViews.csv.columns.renewalDate', (row) => row.renewalDate, 'date'),
    // The phone numbers as the API returns them, as legacy's report printed them.
    reportOnly('homePhone', 6, 'memberViews.csv.columns.phone', (row) => row.phone),
    reportOnly('cellPhone', 7, 'memberViews.csv.columns.cellPhone', (row) => row.mobilePhone),
    reportOnly('email', 8, 'memberViews.csv.columns.email', (row) => row.email),
    reportOnly('status', 10, 'memberViews.csv.columns.status', (row) => row.memberStatus),
    reportOnly('birthday', 12, 'memberViews.csv.columns.birthday', (row) => row.nextBirthday, 'date'),
    reportOnly('age', 13, 'memberViews.csv.columns.age', (row) => row.age, 'number'),
    reportOnly('insurance', 14, 'memberViews.csv.columns.insurance', (row) => row.insuranceName),
    reportOnly(
      'paymentSchedule',
      15,
      'memberViews.csv.columns.paymentSchedule',
      (row) => row.currentAgreementPaymentSchedule,
    ),
    // The current agreement's fee, not the membership's list price.
    reportOnly(
      'membershipFee',
      16,
      'memberViews.csv.columns.membershipFee',
      (row) => row.currentAgreementMsrp,
      'currency',
    ),
    reportOnly('autoPay', 17, 'memberViews.csv.columns.autoPay', (row) => row.autoPay, 'yesBlank'),
    reportOnly('lastPaymentDate', 18, 'memberViews.csv.columns.lastPaymentDate', (row) => row.lastPaymentDate, 'date'),
    reportOnly(
      'lastPaymentAmount',
      19,
      'memberViews.csv.columns.lastPaymentAmount',
      (row) => row.lastPaymentAmount,
      'currency',
    ),
    reportOnly('amountDue', 20, 'memberViews.csv.columns.amountDue', (row) => row.currentBalanceFormula, 'currency'),
    reportOnly('daysPastDue', 21, 'memberViews.csv.columns.daysPastDue', (row) => row.billingDaysPastDue, 'number'),
    // "No" survives here, unlike Auto Pay: legacy printed this field's own Yes/No.
    reportOnly(
      'billToCorporateAccount',
      22,
      'memberViews.csv.columns.billToCorporateAccount',
      (row) => row.billToNationalAccount,
      'yesNoBlank',
    ),
    reportOnly('corporateAccount', 23, 'memberViews.csv.columns.corporateAccount', (row) => row.nationalAccountName),
  ],
};
